import { createContext, ReactNode, useContext } from 'react';

import { OptionMarket } from '#/api/markets';
import { useOptionMarketsState } from './markets-context';

const MarketCtx = createContext<OptionMarket | null>(null);
MarketCtx.displayName = 'MarketCtx';

interface ProviderProps {
  /** Symbol of the market to be provided via context */
  readonly symbol: string | null;
  readonly children: ReactNode;
}

/**
 * Provides the `OptionMarket` for the given market symbol.
 * `null` is provided if a market with given symbol is not
 * present in the option markets state.
 */
export function MarketProvider(props: ProviderProps) {
  const marketsState = useOptionMarketsState();
  const market =
    props.symbol != null
      ? marketsState.markets.entities[props.symbol] ?? null
      : null;
  return (
    <MarketCtx.Provider value={market}>{props.children}</MarketCtx.Provider>
  );
}

export function useMaybeMarket() {
  return useContext(MarketCtx);
}

export function useMarket() {
  const value = useContext(MarketCtx);
  if (value === null) {
    throw new Error('Market symbol not provided or market was not found');
  }
  return value;
}
